import { useCallback, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { Check, Close, KeyboardArrowDown } from '@nine-thirty-five/material-symbols-react/rounded'
import { Modal, ModalActionButton } from '../../../../../components/Modal'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  labelCreateSchema,
  useCreateLabel,
  type LabelFormData,
  useLabelsStore,
  useUpdateLabel,
} from '@zendia/frontend-shared'
import { axiosAuthenticatedRequest } from '../../../../../utilities/axiosClient'
import {
  findLabelColorByBackground,
  getNextLabelColor,
  LABEL_COLORS,
  type LabelColor,
} from '../../../../../components/Labels/labelColors.js'

export const Route = createFileRoute('/_authenticated/settings/labels/$labelId/modal')({
  component: LabelModal,
  staticData: { suppressNotifications: true },
})

function LabelModal() {
  const { labelId } = Route.useParams()
  const { labels } = useLabelsStore()
  const router = useRouter()
  const { mutateAsync: createLabel } = useCreateLabel(axiosAuthenticatedRequest)
  const { mutateAsync: updateLabel } = useUpdateLabel(axiosAuthenticatedRequest)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = labelId !== 'new'

  const labelInitialData = !isEditMode ? null : labels.find((l) => l.id === labelId)

  // New labels get the next unused color from the shared palette so each
  // label a user creates starts out visually distinct; existing labels keep
  // their own colors until the user changes them below.
  const defaultColor = isEditMode ? null : getNextLabelColor(labels)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm<LabelFormData>({
    resolver: zodResolver(labelCreateSchema),
    defaultValues: {
      value: '',
      foregroundColor: defaultColor?.foregroundColor ?? '#000000',
      backgroundColor: defaultColor?.backgroundColor ?? '#FFFFFF',
      ...labelInitialData,
    },
    mode: 'onChange',
  })

  // Watch color values for preview
  const watchedValues = watch()

  // The form still stores the two hex values the API expects; the dropdown
  // just sets them as a pair. A label whose background is not in the palette
  // (saved before the palette existed) leaves the dropdown unselected.
  const selectedColor = findLabelColorByBackground(watchedValues.backgroundColor ?? '')

  const onColorChange = useCallback(
    (color: LabelColor) => {
      setValue('backgroundColor', color.backgroundColor, { shouldValidate: true })
      setValue('foregroundColor', color.foregroundColor, { shouldValidate: true })
    },
    [setValue],
  )

  const onDismiss = useCallback(() => {
    router.history.back()
  }, [router])

  const handleCancel = useCallback(() => {
    reset()
    onDismiss()
  }, [reset, onDismiss])

  const onSubmit = useCallback(
    async (data: LabelFormData) => {
      try {
        setIsSubmitting(true)

        const validatedData = labelCreateSchema.parse(data)
        if (isEditMode) {
          await updateLabel({ labelId, data: validatedData })
        } else {
          await createLabel(validatedData)
        }

        reset()
        router.navigate({ to: '/settings/labels' })
      } catch (error) {
        console.error('Error submitting label form:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [reset, createLabel, updateLabel, router, isEditMode, labelId],
  )

  return (
    <Modal
      open={true}
      onDismiss={handleCancel}
      leftAction={
        <ModalActionButton
          onClick={handleCancel}
          label="Close dialog"
          icon={<Close className="m-auto" />}
          // disabled={isSubmitting}
        />
      }
      rightAction={
        <ModalActionButton
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit(onSubmit)}
          icon={<Check className="m-auto" />}
          label="Save journal entry"
          primary
        />
      }
    >
      <div className="px-6 pt-16 sm:pb-6 sm:mx-6 h-full">
        <div className="pt-4">
          <h2 className="text-2xl font-semibold mb-6 font-serif">
            {isEditMode ? 'Edit' : 'Create New'} Label
          </h2>

          <div className="flex sm:flex-row flex-col gap-4 mb-6">
            <div className="mb-6 sm:w-1/3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preview
              </label>
              <div
                className="inline-block px-3 py-1 rounded-full text-sm font-medium border"
                style={{
                  color: watchedValues.foregroundColor,
                  backgroundColor: watchedValues.backgroundColor,
                  borderColor: watchedValues.foregroundColor + '40', // 25% opacity
                }}
              >
                {watchedValues.value || 'Label Preview'}
              </div>
            </div>
            <form className="space-y-4 flex-1">
              {/* Label Text */}
              <div>
                <label
                  htmlFor="value"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Label Text
                </label>
                <input
                  id="value"
                  type="text"
                  {...register('value')}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 ${
                    errors.value
                      ? 'border-red-500 dark:border-red-400'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Enter label text"
                  disabled={isSubmitting}
                  aria-invalid={errors.value ? 'true' : 'false'}
                  aria-describedby={errors.value ? 'value-error' : undefined}
                />
                {errors.value && (
                  <p
                    id="value-error"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {errors.value.message}
                  </p>
                )}
              </div>

              {/* Color */}
              <div>
                <label
                  htmlFor="labelColor"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Color
                </label>
                <div className="relative w-full">
                  <Listbox value={selectedColor} onChange={onColorChange}>
                    <ListboxButton
                      id="labelColor"
                      disabled={isSubmitting}
                      className="relative w-full flex items-center gap-2 cursor-pointer appearance-none px-3 py-2 pe-9 text-left border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                    >
                      <span
                        className="size-4 shrink-0 rounded-sm border border-black/10 dark:border-white/20"
                        style={{ backgroundColor: watchedValues.backgroundColor }}
                        aria-hidden="true"
                      />
                      <span>{selectedColor?.name ?? 'Custom'}</span>
                    </ListboxButton>
                    <KeyboardArrowDown
                      className="pointer-events-none absolute top-2 right-2 size-6 fill-gray-700 dark:fill-white/60"
                      aria-hidden="true"
                    />
                    <ListboxOptions
                      anchor="bottom"
                      className="z-50 w-(--button-width) max-h-64 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-900 shadow-lg mt-1 p-1 focus:outline-none"
                    >
                      {LABEL_COLORS.map((color) => (
                        <ListboxOption
                          key={color.backgroundColor}
                          value={color}
                          className="flex items-center gap-2 px-3 py-2 rounded-sm cursor-pointer data-focus:bg-gray-100 dark:data-focus:bg-white/10"
                        >
                          <span
                            className="size-4 shrink-0 rounded-sm border border-black/10 dark:border-white/20"
                            style={{ backgroundColor: color.backgroundColor }}
                            aria-hidden="true"
                          />
                          {color.name}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                </div>
                {(errors.backgroundColor ?? errors.foregroundColor) && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                    {errors.backgroundColor?.message ?? errors.foregroundColor?.message}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  )
}
